import React from "react";
import { AlertTriangle, CheckCircle2, PlayCircle } from "lucide-react";

import FlowInspectorPanel from "./FlowInspectorPanel";
import BookingTestPanel from "./BookingTestPanel";

import type {
  CustomerConfig,
  Environment,
  FlowStep,
  TestBookingState,
  VariableRow,
} from "../types";

type TriggerType = "email" | "recurrence" | "http";

type Props = {
  selectedStep?: FlowStep | null;
  triggerType: TriggerType;
  setTriggerType: React.Dispatch<React.SetStateAction<TriggerType>>;
  removeSelectedStep: () => void;
  openPayloadEditor: () => void;

  customersLoading: boolean;
  customersError?: string | null;
  selectedCustomer?: CustomerConfig | null;
  environment: Environment;
  setEnvironment: React.Dispatch<React.SetStateAction<Environment>>;
  customerSearch: string;
  setCustomerSearch: (value: string) => void;
  customerPickerOpen: boolean;
  setCustomerPickerOpen: (value: boolean) => void;
  filteredCustomers: CustomerConfig[];
  selectCustomer: (customer: CustomerConfig) => void;

  payloadVariables: VariableRow[];
  payloadArrays: VariableRow[];

  payloadMappingEnabled: boolean;
  setPayloadMappingEnabled: (value: boolean) => void;

  selectedTargetPayload: {
    label: string;
    description: string;
  };

  missingRequiredCount: number;
  openMapper: () => void;

  bookingPayloadPreview: string;
  payloadIsValid: boolean;
  testCreateBooking: () => void;
  testBookingState: TestBookingState;
  openOutputPreview: () => void;
};

export default function FlowBuilderInspector(props: Props) {
  const selectedStep = props.selectedStep;

  return (
    <aside className="w-[420px] shrink-0 overflow-y-auto border-l border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-5 dark:border-white/10">
        <div className="flex items-center gap-3">
          <div className="rounded-xl bg-orange-500/10 p-3 text-orange-600 dark:text-orange-300">
            <PlayCircle className="h-5 w-5" />
          </div>

          <div className="min-w-0">
            <h3 className="truncate font-black text-slate-950 dark:text-white">
              {selectedStep?.title || "Selected action"}
            </h3>
            <div className="mt-1 truncate font-mono text-xs text-slate-500 dark:text-slate-400">
              {selectedStep?.actionType || "No action selected"}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6 p-5">
        <div>
          <div className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">
            Description
          </div>
          <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
            {selectedStep?.subtitle || "Configureer de geselecteerde workflow action."}
          </p>
        </div>

        <div>
          <div className="mb-2 text-xs font-black uppercase tracking-wide text-slate-400">
            Configuration
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-950">
            <FlowInspectorPanel {...props} />
          </div>
        </div>

        <div>
          <div className="mb-3 text-xs font-black uppercase tracking-wide text-slate-400">
            Actions
          </div>

          <div className="space-y-3">
            {selectedStep?.actionType === "trigger.http" && (
              <button
                type="button"
                onClick={props.openPayloadEditor}
                className="w-full rounded-xl bg-orange-500 px-4 py-3 text-sm font-black text-white transition hover:bg-orange-400"
              >
                Payload invoeren
              </button>
            )}

            {selectedStep?.actionType === "jdx.create-shipment-booking" && (
              <button
                type="button"
                onClick={props.openMapper}
                className="w-full rounded-xl bg-orange-500 px-4 py-3 text-sm font-black text-white transition hover:bg-orange-400"
              >
                Mapping instellen
              </button>
            )}

            <button
              type="button"
              onClick={props.testCreateBooking}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-white/10"
            >
              Validate action
            </button>

            <button
              type="button"
              onClick={props.openOutputPreview}
              className="w-full rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-700 transition hover:bg-amber-100 dark:border-yellow-500/30 dark:bg-yellow-500/10 dark:text-yellow-300 dark:hover:bg-yellow-500/20"
            >
              Preview output
            </button>
          </div>
        </div>

        <div>
          <div className="mb-3 text-xs font-black uppercase tracking-wide text-slate-400">
            Validation
          </div>

          <div className="space-y-3">
            <div
              className={`flex items-center gap-3 rounded-xl border p-3 ${
                props.payloadIsValid
                  ? "border-emerald-200 bg-emerald-50 dark:border-green-500/20 dark:bg-green-500/10"
                  : "border-red-200 bg-red-50 dark:border-red-500/20 dark:bg-red-500/10"
              }`}
            >
              <CheckCircle2
                className={`h-4 w-4 ${
                  props.payloadIsValid
                    ? "text-emerald-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              />
              <span
                className={`text-sm ${
                  props.payloadIsValid
                    ? "text-emerald-700 dark:text-green-300"
                    : "text-red-700 dark:text-red-300"
                }`}
              >
                Payload {props.payloadIsValid ? "valid" : "invalid"}
              </span>
            </div>

            <div
              className={`flex items-center gap-3 rounded-xl border p-3 ${
                props.missingRequiredCount === 0
                  ? "border-emerald-200 bg-emerald-50 dark:border-green-500/20 dark:bg-green-500/10"
                  : "border-amber-200 bg-amber-50 dark:border-yellow-500/20 dark:bg-yellow-500/10"
              }`}
            >
              {props.missingRequiredCount === 0 ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-green-400" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-yellow-400" />
              )}

              <span
                className={`text-sm ${
                  props.missingRequiredCount === 0
                    ? "text-emerald-700 dark:text-green-300"
                    : "text-amber-700 dark:text-yellow-300"
                }`}
              >
                {props.missingRequiredCount === 0
                  ? "Mapping complete"
                  : `${props.missingRequiredCount} required mappings missing`}
              </span>
            </div>
          </div>
        </div>

        <div>
          <div className="mb-3 text-xs font-black uppercase tracking-wide text-slate-400">
            Test booking
          </div>

          <BookingTestPanel
            selectedCustomer={props.selectedCustomer}
            payloadIsValid={props.payloadIsValid}
            bookingPayloadPreview={props.bookingPayloadPreview}
            testCreateBooking={props.testCreateBooking}
            testBookingState={props.testBookingState}
            openOutputPreview={props.openOutputPreview}
          />
        </div>
      </div>
    </aside>
  );
}